import type {
    ObjectShorthandValue,
    ProjectTileProps,
} from '@archipro-design/aria';
import { useRef } from 'react';
import type { AdvertCreative } from '@archipro-website/tracker';
import { useAdEventHandler, useLogImpression } from '@archipro-website/tracker';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

type TileProps = ObjectShorthandValue<ProjectTileProps> & {
    advertCreative: AdvertCreative;
};

/**
 * ProjectAdvertTile component renders a tile for project advertisements.
 * It handles ad click events and logs impressions.
 *
 * @param {Object} props - The properties for the component.
 * @param {Function} props.onClick - Callback function for click events.
 * @param {AdvertCreative} props.advertCreative - The advertisement creative data.
 * @param {Object} rest - Additional properties for the component.
 */
const ProjectAdvertTile = ({ onClick, advertCreative, ...rest }: TileProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const tileRef = useRef<HTMLDivElement | null>(null);
    const { onClick: adOnClick } = useAdEventHandler(advertCreative, ref);
    const { handleFollow, isInDesignBoard } = useHandleFollow();

    /**
     * Handles the click event for the tile, triggering ad click tracking.
     */
    const tileOnClick = () => {
        adOnClick('Ad_CTAClick');
        onClick?.();
    };

    useLogImpression({
        data: rest.impression,
        ref: tileRef,
    });

    const tile: TileViewModel<ProjectTileViewModel> = {
        model: {
            id: advertCreative.parent.id,
            title: advertCreative.title,
            images: [
                {
                    src: (rest.image[0] as { src: string })?.src || '',
                    alt: rest.projectName as string,
                },
            ],
            professional: {
                id: advertCreative.professional.id,
                name: advertCreative.professional.title,
                link: advertCreative.professional.href,
            },
            link: advertCreative.cta.href,
            video: [],
            hasAwards: false,
            badge: 'Sponsored',
        },
        isFollowed: isInDesignBoard(advertCreative.parent.id, 'ProjectPin'),
        onFollow: (pinId, pinType) => handleFollow({ id: pinId }, pinType),
    };

    return (
        <div ref={tileRef} onClick={tileOnClick}>
            <Tile tile={tile} />
        </div>
    );
};

export default ProjectAdvertTile;
